import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { apiGetOrgProfile, apiUpdateOrgProfile, apiGetOrgQuota, OrgQuota } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';

const schema = z.object({
  name: z.string().min(1, "Le nom de l'organisation est requis"),
  logoUrl: z.string().url("URL invalide").or(z.literal('')),
  senderEmail: z.string().email("Format d'email invalide").or(z.literal('')),
});

type FormValues = z.infer<typeof schema>;

export function SettingsOrg() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [quota, setQuota] = useState<OrgQuota | null>(null);
  const [quotaError, setQuotaError] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    mode: 'onBlur',
    resolver: zodResolver(schema),
  });

  useEffect(() => {
    (async () => {
      try {
        const [org, quotaResult] = await Promise.all([
          apiGetOrgProfile(),
          apiGetOrgQuota().catch(() => { setQuotaError(true); return null; }),
        ]);
        reset({
          name: org.name,
          logoUrl: org.logoUrl ?? '',
          senderEmail: org.senderEmail ?? '',
        });
        if (quotaResult) setQuota(quotaResult);
      } catch {
        toast.error("Impossible de charger les paramètres organisation", { duration: Infinity });
      } finally {
        setLoading(false);
      }
    })();
  }, [reset]);

  const onSubmit = async (data: FormValues) => {
    setSaving(true);
    try {
      await apiUpdateOrgProfile(data);
      toast.success("Profil organisation mis à jour", { duration: 4000 });
    } catch {
      toast.error("Erreur lors de la mise à jour du profil", { duration: Infinity });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4 max-w-lg">
        <div className="h-6 w-48 animate-pulse bg-gray-200 rounded" />
        <div className="h-4 w-72 animate-pulse bg-gray-100 rounded" />
        <div className="h-32 animate-pulse bg-gray-100 rounded-lg border" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold">Profil Organisation</h2>
        <p className="text-sm text-muted-foreground">
          Ces informations sont utilisées comme identité expéditeur dans toutes les communications fournisseurs.
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 bg-white/70 border rounded-lg p-4 max-w-lg">
        <div>
          <Label htmlFor="org-name">Nom de l'organisation *</Label>
          <Input
            id="org-name"
            {...register('name')}
            placeholder="Acme Corp"
          />
          {errors.name && (
            <p className="text-sm text-red-600 mt-1">{errors.name.message}</p>
          )}
        </div>

        <div>
          <Label htmlFor="org-logo">Logo (URL)</Label>
          <Input
            id="org-logo"
            type="text"
            {...register('logoUrl')}
            placeholder="https://exemple.com/logo.png"
          />
          {errors.logoUrl && (
            <p className="text-sm text-red-600 mt-1">{errors.logoUrl.message}</p>
          )}
        </div>

        <div>
          <Label htmlFor="org-sender-email">Email expéditeur</Label>
          <Input
            id="org-sender-email"
            type="email"
            {...register('senderEmail')}
            placeholder="noreply@acme.com"
          />
          {errors.senderEmail && (
            <p className="text-sm text-red-600 mt-1">{errors.senderEmail.message}</p>
          )}
          <p className="text-xs text-muted-foreground mt-1">
            Email utilisé comme expéditeur des invitations et relances fournisseurs.
          </p>
        </div>

        <Button
          type="submit"
          className="bg-green-600 hover:bg-green-700"
          disabled={saving}
        >
          {saving ? 'Enregistrement...' : 'Enregistrer les modifications'}
        </Button>
      </form>

      <div className="space-y-3">
        <div>
          <h2 className="text-lg font-semibold">Quota Extraction IA</h2>
          <p className="text-sm text-muted-foreground">
            Consommation mensuelle d'extractions IA pour votre organisation.
          </p>
        </div>

        {quota ? (
          <div className="bg-white/70 border rounded-lg p-4 max-w-lg space-y-2">
            <p className="text-sm font-medium">
              {quota.aiQuotaUsed} / {quota.aiQuotaLimit} extractions utilisées ce mois
            </p>
            <p className="text-xs text-muted-foreground">
              Remise à zéro le {new Date(quota.aiQuotaResetAt).toLocaleDateString('fr-FR', {
                day: '2-digit', month: 'long', year: 'numeric',
              })}
            </p>
          </div>
        ) : quotaError ? (
          <p className="text-sm text-muted-foreground">Impossible de charger les données quota.</p>
        ) : null}
      </div>
    </div>
  );
}
